import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import type { Pagination, SortItem } from "./commonApiTypes";
import type { Display } from "./DisplayService";
import type { RunningMessage } from "./RunningMessageService";
import { User } from "./UserService";
import { ApiService } from "./ApiService";

// GraphQL queries
const DISPLAY_RUNNING_MESSAGE_LIST_QUERY = gql`
  query DisplayRunningMessageList(
    $filter: DisplayRunningMessageFilter
    $params: PaginationAndSorting
  ) {
    displayRunningMessageList(filter: $filter, params: $params) {
      code
      data {
        id
        displayId
        runningMessageId
        startDatetime
        endDatetime
        orderNo
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        runningMessage {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_RUNNING_MESSAGE_GET_QUERY = gql`
  query DisplayRunningMessageGet($id: ID) {
    displayRunningMessageGet(id: $id) {
      code
      data {
        id
        displayId
        runningMessageId
        startDatetime
        endDatetime
        orderNo
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        runningMessage {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_RUNNING_MESSAGE_GET_MANY_QUERY = gql`
  query DisplayRunningMessageGetMany($ids: [ID]) {
    displayRunningMessageGetMany(ids: $ids) {
      code
      data {
        id
        displayId
        runningMessageId
        startDatetime
        endDatetime
        orderNo
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        runningMessage {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const DISPLAY_RUNNING_MESSAGE_CREATE_MUTATION = gql`
  mutation DisplayRunningMessageCreate($params: DisplayRunningMessageInput) {
    displayRunningMessageCreate(params: $params) {
      code
      data {
        id
        displayId
        runningMessageId
        startDatetime
        endDatetime
        orderNo
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        runningMessage {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_RUNNING_MESSAGE_UPDATE_MUTATION = gql`
  mutation DisplayRunningMessageUpdate(
    $where: DisplayRunningMessageWhereInput
    $params: DisplayRunningMessageInput
  ) {
    displayRunningMessageUpdate(where: $where, params: $params) {
      code
      data {
        id
        displayId
        runningMessageId
        startDatetime
        endDatetime
        orderNo
        createdAt
        createdBy
        userCreated {
          id
        }
        display {
          id
        }
        runningMessage {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_RUNNING_MESSAGE_REMOVE_MUTATION = gql`
  mutation DisplayRunningMessageRemove(
    $where: DisplayRunningMessageWhereInput
  ) {
    displayRunningMessageRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_RUNNING_MESSAGE_REMOVE_MANY_MUTATION = gql`
  mutation DisplayRunningMessageRemoveMany(
    $where: [DisplayRunningMessageWhereInput]
  ) {
    displayRunningMessageRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types

export interface DisplayRunningMessage {
  id: string;
  displayId?: string;
  runningMessageId?: string;
  startDatetime?: string;
  endDatetime?: string;
  orderNo?: number;
  createdAt?: string;
  createdBy?: string;
  userCreated?: User;
  display?: Display;
  runningMessage?: RunningMessage;
}

export interface DisplayRunningMessageFilter {
  id?: string[];
  displayId?: string[];
  runningMessageId?: string[];
  startDatetime?: string;
  endDatetime?: string;
}

export interface DisplayRunningMessageInput {
  displayId?: string | null | undefined;
  runningMessageId?: string | null | undefined;
  startDatetime?: string | null | undefined;
  endDatetime?: string | null | undefined;
  orderNo?: number | null | undefined;
}

export interface DisplayRunningMessageWhereInput {
  id: string;
}

/**
 * Display Running Message service that handles all display running message-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class DisplayRunningMessageService {
  /**
   * Get a list of display running messages with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: DisplayRunningMessageFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      DISPLAY_RUNNING_MESSAGE_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single display running message by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      DISPLAY_RUNNING_MESSAGE_GET_QUERY,
      {
        id: id,
      }
    );
    return result;
  }

  /**
   * Get multiple display running messages by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      DISPLAY_RUNNING_MESSAGE_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new display running message
   */
  static async create(params: DisplayRunningMessageInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_RUNNING_MESSAGE_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing display running message
   */
  static async update(
    id: string,
    params: DisplayRunningMessageInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_RUNNING_MESSAGE_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a display running message
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_RUNNING_MESSAGE_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple display running messages
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      DISPLAY_RUNNING_MESSAGE_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
